import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/dateFormat';

interface Company {
  id: string;
  name: string;
  industry: string;
  description: string;
  url: string;
  culture: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  created_at: string;
  profile_photo_url: string | null;
  language: string;
  timezone: string;
  organisation_id: string;
  notification_preferences: Record<string, unknown>;
  company_id: string;
  company?: Company; // Optional since it's from a join
}

type SortField = 'name' | 'company' | 'created_at';
type SortDirection = 'asc' | 'desc';

const Dashboard: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it with default desc direction
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedClients = () => {
    return [...clients].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'name':
          return ((a.name || '') > (b.name || '') ? 1 : -1) * direction;
        case 'company':
          return ((a.company?.name || '') > (b.company?.name || '') ? 1 : -1) * direction;
        case 'created_at':
          return ((a.created_at || '') > (b.created_at || '') ? 1 : -1) * direction;
        default:
          return 0;
      }
    });
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        console.log('Fetching clients...');
        // First fetch clients
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select(`
            id,
            name,
            email,
            phone_number,
            created_at,
            profile_photo_url,
            language,
            timezone,
            organisation_id,
            notification_preferences,
            company_id
          `)
          .order('created_at', { ascending: false });

        if (clientsError) {
          console.error('Supabase error:', clientsError);
          throw clientsError;
        }

        // Then fetch companies
        const companyIds = clientsData?.map(client => client.company_id).filter(Boolean) || [];
        if (companyIds.length > 0) {
          console.log('Company IDs:', companyIds);
          const { data: companiesData, error: companiesError } = await supabase
            .from('company')
            .select(`
              id,
              name,
              industry,
              description,
              about_url,
              url,
              culture
            `)
            .in('id', companyIds);

          console.log('Companies data:', companiesData);
          if (companiesError) throw companiesError;

          // Combine the data
          const clientsWithCompanies = clientsData.map(client => {
            const company = companiesData?.find(c => c.id === client.company_id);
            console.log(`Client ${client.id} company:`, company);
            return {
              ...client,
              company
            };
          });

          console.log('Final clients with companies:', clientsWithCompanies);
          setClients(clientsWithCompanies);
        } else {
          setClients(clientsData || []);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const sortedClients = getSortedClients();

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
      
      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Total Clients</h2>
          <p className="text-3xl font-bold text-blue-600">{clients.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Coming Soon</h2>
          <p className="text-3xl font-bold text-green-600">-</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Coming Soon</h2>
          <p className="text-3xl font-bold text-purple-600">-</p>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Clients</h3>
          <span className="text-sm text-gray-500">Total: {clients.length}</span>
        </div>
        <div className="border-t border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="text-center py-4">Loading clients...</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      Name
                      {sortField === 'name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('company')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      Company
                      {sortField === 'company' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th 
                    onClick={() => handleSort('created_at')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      Created
                      {sortField === 'created_at' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {client.profile_photo_url ? (
                          <img 
                            className="h-10 w-10 rounded-full mr-3"
                            src={client.profile_photo_url}
                            alt={client.name || 'Client'}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 mr-3 flex items-center justify-center">
                            <span className="text-gray-500 text-sm">
                              {(client.name || 'N/A').charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {client.name || 'Unnamed Client'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.company ? (
                        <div>
                          <div className="text-sm text-gray-900">{client.company.name}</div>
                          <div className="flex items-center gap-1 mt-1">
                            {client.company.industry && (
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                                {client.company.industry}
                              </span>
                            )}
                            {client.company.url && (
                              <a 
                                href={client.company.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline"
                              >
                                Website
                              </a>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No company</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email || 'No email'}</div>
                      <div className="text-sm text-gray-500">{client.phone_number || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.language || 'Not set'}</div>
                      <div className="text-sm text-gray-500">{client.timezone || 'No timezone'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.created_at ? formatDate(client.created_at) : 'No date'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 